



import React, { useState, useEffect, Component, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { EMPLOYER_URL } from "../../resources/resource";
import Chart from 'chart.js/auto';
import { CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement } from 'chart.js';
import { BiX, BiCheck } from 'react-icons/bi';

// Register Chart.js components
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement);

// Error Boundary Component
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-red-600">
          Error rendering component: {this.state.error?.message || "Unknown error"}
        </div>
      );
    }
    return this.props.children;
  }
}

// EmployerEmployee Component
const EmployerEmployee = () => {
  const currentUser = useSelector((state) => state.user.currentUser);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const chartRefs = useRef({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/${currentUser?._id}`);
        const data = await response.json();
        if (response.ok) {
          setEmployees(Array.isArray(data) ? data : [data]);
        } else {
          setError(data.error || 'Failed to fetch employees');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser?._id) {
      fetchEmployees();
    }
  }, [currentUser]);

  useEffect(() => {
    if (employees.length > 0) {
      createCharts();
    }
    return () => {
      Object.values(chartRefs.current).forEach(chart => chart?.destroy());
      chartRefs.current = {};
    };
  }, [employees]);

  const createCharts = () => {
    Object.values(chartRefs.current).forEach(chart => {
      if (chart) {
        chart.destroy();
      }
    });
    chartRefs.current = {};

    // Pie Chart for Employee Status Distribution
    const statusCounts = employees.reduce((acc, emp) => {
      acc[emp.status] = (acc[emp.status] || 0) + 1;
      return acc;
    }, {});
    const ctxStatus = document.getElementById('statusChart')?.getContext('2d');
    if (ctxStatus) {
      chartRefs.current.status = new Chart(ctxStatus, {
        type: 'pie',
        data: {
          labels: Object.keys(statusCounts),
          datasets: [{
            data: Object.values(statusCounts),
            backgroundColor: ['#36A2EB', '#FF6384'],
          }],
        },
        options: { responsive: true, plugins: { legend: { position: 'top' } } },
      });
    }

    // Bar Chart for Attendance Stats
    const attendanceStats = employees.map(emp => ({
      name: `${emp.firstName} ${emp.lastName}`,
      present: emp.attendance.filter(a => a.status === 'Present').length,
      absent: emp.attendance.filter(a => a.status === 'Absent').length,
    }));
    const ctxAttendance = document.getElementById('attendanceChart')?.getContext('2d');
    if (ctxAttendance) {
      chartRefs.current.attendance = new Chart(ctxAttendance, {
        type: 'bar',
        data: {
          labels: attendanceStats.map(s => s.name),
          datasets: [
            { label: 'Present', data: attendanceStats.map(s => s.present), backgroundColor: 'rgba(75, 192, 192, 0.6)' },
            { label: 'Absent', data: attendanceStats.map(s => s.absent), backgroundColor: 'rgba(255, 99, 132, 0.6)' },
          ],
        },
        options: { responsive: true, scales: { y: { beginAtZero: true } } },
      });
    }

    // Line Chart for Leave Trends (example: number of leaves per month)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const leaveCounts = months.map(m => employees.reduce((sum, emp) => sum + emp.leaveRequests.filter(l => new Date(l.startDate).toLocaleString('default', { month: 'short' }) === m).length, 0));
    const ctxLeaves = document.getElementById('leavesChart')?.getContext('2d');
    if (ctxLeaves) {
      chartRefs.current.leaves = new Chart(ctxLeaves, {
        type: 'line',
        data: {
          labels: months,
          datasets: [{
            label: 'Leaves per Month',
            data: leaveCounts,
            borderColor: '#FF6384',
            tension: 0.1,
            fill: false,
          }],
        },
        options: { responsive: true, scales: { y: { beginAtZero: true } } },
      });
    }

    // Waterfall Chart Simulation for Queries/Remarks Count (cumulative)
    const averageQueries = employees.reduce((sum, e) => sum + e.queries.length, 0) / employees.length || 0;
    const averageRemarks = employees.reduce((sum, e) => sum + e.remarks.length, 0) / employees.length || 0;
    const waterfallData = [
      { label: 'Queries', value: [0, averageQueries] },
      { label: 'Remarks', value: [averageQueries, averageQueries + averageRemarks] },
    ];
    const ctxWaterfall = document.getElementById('waterfallChart')?.getContext('2d');
    if (ctxWaterfall) {
      chartRefs.current.waterfall = new Chart(ctxWaterfall, {
        type: 'bar',
        data: {
          labels: waterfallData.map(d => d.label),
          datasets: [{
            label: 'Average Counts',
            data: waterfallData.map(d => d.value),
            backgroundColor: ['#4CAF50', '#2196F3'],
          }],
        },
        options: {
          responsive: true,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true },
            x: { stacked: true },
          },
        },
      });
    }
  };

  const handleLeaveAction = async (employeeId, leaveId, action, response) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}employer-employee/${action}-leave/${currentUser._id}/${employeeId}/${leaveId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ response }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        setEmployees(employees.map(emp => emp._id === employeeId ? {
          ...emp,
          leaveRequests: emp.leaveRequests.map(l => l._id === leaveId ? data.leave : l),
        } : emp));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleQuery = async (employeeId, description) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/query/${currentUser._id}/${employeeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(employees.map(emp => emp._id === employeeId ? { ...emp, queries: [...emp.queries, data.query] } : emp));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAttendance = async (employeeId, { date, status, notes }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/attendance/${currentUser._id}/${employeeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, status, notes }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(employees.map(emp => emp._id === employeeId ? { ...emp, attendance: [...emp.attendance, data.attendance] } : emp));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemark = async (employeeId, { month, year, comment }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/remark/${currentUser._id}/${employeeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month, year, comment }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(employees.map(emp => emp._id === employeeId ? { ...emp, remarks: [...emp.remarks, data.remark] } : emp));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusUpdate = async (employeeId, { status, terminationReason }) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}employer-employee/status/${currentUser._id}/${employeeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, terminationReason }),
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(employees.map(emp => emp._id === employeeId ? data.employee : emp));
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  const totalEmployees = employees.length;
  const pendingLeaves = employees.reduce((sum, emp) => sum + emp.leaveRequests.filter(l => l.status === 'Pending').length, 0);
  const averageAttendance = employees.length > 0 ? employees.reduce((sum, emp) => sum + emp.attendance.length, 0) / employees.length : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
       
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700">Total Employees</h3>
            <p className="text-3xl font-bold text-indigo-600">{totalEmployees}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700">Pending Leaves</h3>
            <p className="text-3xl font-bold text-red-600">{pendingLeaves}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700">Average Attendance Records</h3>
            <p className="text-3xl font-bold text-blue-600">{averageAttendance.toFixed(1)}</p>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Employee Status (Pie Chart)</h3>
            <div className="h-80">
              <canvas id="statusChart"></canvas>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Attendance Stats (Bar Chart)</h3>
            <div className="h-80">
              <canvas id="attendanceChart"></canvas>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Leaves Trend (Line Chart)</h3>
            <div className="h-80">
              <canvas id="leavesChart"></canvas>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Average Counts (Waterfall Chart)</h3>
            <div className="h-80">
              <canvas id="waterfallChart"></canvas>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg mb-8 overflow-x-auto">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Employees</h3>
          <table className="w-full text-left text-sm md:text-base">
            <thead className="bg-indigo-100 text-indigo-800">
              <tr>
                <th className="py-3 px-6">Name</th>
                <th className="py-3 px-6">Email</th>
                <th className="py-3 px-6">Status</th>
                <th className="py-3 px-6">Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp._id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-6">{`${emp.firstName} ${emp.lastName}`}</td>
                  <td className="py-3 px-6">{emp.email}</td>
                  <td className="py-3 px-6">{emp.status}</td>
                  <td className="py-3 px-6">
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => setSelectedEmployee(emp)}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white p-8 rounded-xl max-h-[80vh] overflow-y-auto shadow-2xl w-full max-w-4xl">
              <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10">
                <h3 className="text-2xl font-bold text-gray-800">
                  Manage {selectedEmployee.firstName} {selectedEmployee.lastName}
                </h3>
                <button onClick={() => setSelectedEmployee(null)} className="text-gray-500 hover:text-gray-700">
                  <BiX size={28} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Leave Requests */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2 text-gray-700">Leave Requests</h4>
                  {selectedEmployee.leaveRequests.map(leave => (
                    <div key={leave._id} className="mb-4 p-4 border rounded bg-white">
                      <p><strong>Start Date:</strong> {new Date(leave.startDate).toLocaleDateString()}</p>
                      <p><strong>End Date:</strong> {new Date(leave.endDate).toLocaleDateString()}</p>
                      <p><strong>Reason:</strong> {leave.reason}</p>
                      <p><strong>Status:</strong> {leave.status}</p>
                      {leave.status === 'Pending' && (
                        <div className="mt-2">
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              const response = e.target.response.value;
                              handleLeaveAction(selectedEmployee._id, leave._id, 'approve', response);
                              e.target.reset();
                            }}
                          >
                            <textarea
                              name="response"
                              className="w-full p-2 border rounded mb-2"
                              placeholder="Approval response"
                            />
                            <button
                              type="submit"
                              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mr-2"
                            >
                              Approve
                            </button>
                          </form>
                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              const response = e.target.response.value;
                              handleLeaveAction(selectedEmployee._id, leave._id, 'reject', response);
                              e.target.reset();
                            }}
                          >
                            <textarea
                              name="response"
                              className="w-full p-2 border rounded mb-2"
                              placeholder="Rejection response"
                            />
                            <button
                              type="submit"
                              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </form>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Queries */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2 text-gray-700">Queries</h4>
                  {selectedEmployee.queries.map(query => (
                    <div key={query.queryId} className="mb-4 p-4 border rounded bg-white">
                      <p><strong>Issued:</strong> {new Date(query.dateIssued).toLocaleDateString()}</p>
                      <p><strong>Description:</strong> {query.description}</p>
                      <p><strong>Response:</strong> {query.response || 'Pending'}</p>
                    </div>
                  ))}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const description = e.target.description.value;
                      handleQuery(selectedEmployee._id, description);
                      e.target.reset();
                    }}
                  >
                    <textarea
                      name="description"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Issue new query"
                    />
                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Issue Query
                    </button>
                  </form>
                </div>

                {/* Attendance */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2 text-gray-700">Attendance</h4>
                  {selectedEmployee.attendance.map((att, index) => (
                    <div key={index} className="mb-4 p-4 border rounded bg-white">
                      <p><strong>Date:</strong> {new Date(att.date).toLocaleDateString()}</p>
                      <p><strong>Status:</strong> {att.status}</p>
                      <p><strong>Notes:</strong> {att.notes || 'None'}</p>
                    </div>
                  ))}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const date = e.target.date.value;
                      const status = e.target.status.value;
                      const notes = e.target.notes.value;
                      handleAttendance(selectedEmployee._id, { date, status, notes });
                      e.target.reset();
                    }}
                  >
                    <input
                      type="date"
                      name="date"
                      className="w-full p-2 border rounded mb-2"
                      required
                    />
                    <select name="status" className="w-full p-2 border rounded mb-2" required>
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                      <option value="Late">Late</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                    <textarea
                      name="notes"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Attendance notes"
                    />
                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Record Attendance
                    </button>
                  </form>
                </div>

                {/* Remarks */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2 text-gray-700">Monthly Remarks</h4>
                  {selectedEmployee.remarks.map((remark, index) => (
                    <div key={index} className="mb-4 p-4 border rounded bg-white">
                      <p><strong>Month/Year:</strong> {remark.month} {remark.year}</p>
                      <p><strong>Comment:</strong> {remark.comment}</p>
                    </div>
                  ))}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const month = e.target.month.value;
                      const year = e.target.year.value;
                      const comment = e.target.comment.value;
                      handleRemark(selectedEmployee._id, { month, year, comment });
                      e.target.reset();
                    }}
                  >
                    <input
                      type="text"
                      name="month"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Month (e.g., January)"
                      required
                    />
                    <input
                      type="number"
                      name="year"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Year (e.g., 2025)"
                      required
                    />
                    <textarea
                      name="comment"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Enter remark"
                      required
                    />
                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Add Remark
                    </button>
                  </form>
                </div>

                {/* Status */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-2 text-gray-700">Employee Status</h4>
                  <p><strong>Current Status:</strong> {selectedEmployee.status}</p>
                  {selectedEmployee.status === 'Terminated' && (
                    <p><strong>Termination Reason:</strong> {selectedEmployee.terminationReason}</p>
                  )}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const status = e.target.status.value;
                      const terminationReason = status === 'Terminated' ? e.target.terminationReason.value : '';
                      handleStatusUpdate(selectedEmployee._id, { status, terminationReason });
                      e.target.reset();
                    }}
                  >
                    <select
                      name="status"
                      className="w-full p-2 border rounded mb-2"
                      defaultValue={selectedEmployee.status}
                      required
                    >
                      <option value="Working">Working</option>
                      <option value="Terminated">Terminated</option>
                    </select>
                    <textarea
                      name="terminationReason"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Termination reason (required if Terminated)"
                    />
                    <button
                      type="submit"
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Update Status
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// Placeholder sub-component
const EmployerEmployeeSub1 = () => (
  <div className="p-6">
    <h2 className="text-4xl font-bold text-gray-800 mb-4">Employee Management Sub</h2>
    <div className="bg-white p-6 rounded-xl shadow-lg">
      <p className="text-gray-600">Manage employee records in sub view.</p>
    </div>
  </div>
);

const EmployerDashboard = () => {
  const [activeMenu, setActiveMenu] = useState('employees');
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const currentUser = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const handleMenuClick = (menuName) => {
    setActiveMenu(menuName);
    setActiveSubMenu(null);
    setIsSidebarOpen(false);
  };

  const handleSubMenuClick = (subMenuName) => {
    setActiveSubMenu(subMenuName);
    setIsSidebarOpen(false);
  };

  const menuConfig = [
    {
      name: 'employees',
      label: 'Employees',
      icon: '👷',
      component: <EmployerEmployee />,
      subMenu: [
        { name: 'employees-sub1', label: 'Employee Management', icon: '📋', component: <EmployerEmployeeSub1 /> },
      ],
    },
  ];

  const currentMenu = menuConfig.find((item) => item.name === activeMenu) || menuConfig[0];
  const currentSubMenu = activeSubMenu
    ? currentMenu.subMenu.find((sub) => sub.name === activeSubMenu)
    : null;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 to-gray-200">
      {/* Sidebar */}
   
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-40"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile Header */}
        <header className="md:hidden bg-gray-800 text-white p-4 flex items-center justify-between shadow-lg">
       
          <button
            className="text-white text-3xl"
            onClick={toggleSidebar}
          >
            ☰
          </button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <ErrorBoundary>
            {currentSubMenu ? currentSubMenu.component : currentMenu.component}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};

export default EmployerDashboard;